import { forwardRef, useCallback, useMemo, useRef, useState } from 'react'

import { Flex, HStack, Input, Text } from '@chakra-ui/react'
import Select, { SingleValue } from 'react-select'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { InformationIcon } from '@/components/util-component/InformationIcon'

export enum ActionMode {
  Merge = 'merge',
  Copy = 'copy',
}

const ActionModeEnum = z.nativeEnum(ActionMode)

type Option = {
  value: string
  label: string
}

export type ViewMergeConfirmData = {
  mode:
    | { toViewId: string; actionMode: ActionMode.Merge }
    | { viewName?: string; actionMode: ActionMode.Copy }
}

export type MergeViewDialogData = {
  networks: Array<{ id: string; name: string }>
  currentNetworkId: string
}

type MergeViewDialogProps = {
  mergeDialogData: MergeViewDialogData | undefined
  onClose: () => void
  getViewsByNetworkId: (networkId: string) => Promise<
    Array<{
      id: string
      name: string
    }>
  >
  onConfirm: (fromViewId: string, data: ViewMergeConfirmData) => Promise<void>
}

export const INITIAL_VIEW_MERGE_DATA: ViewMergeConfirmData = {
  mode: { viewName: '', actionMode: ActionMode.Copy },
}

const MergeViewDialog = forwardRef<HTMLDivElement, MergeViewDialogProps>(
  ({ mergeDialogData, onClose, onConfirm, getViewsByNetworkId }, ref) => {
    const cancelRef = useRef(null)

    const [selectedNetworkId, setSelectedNetworkId] = useState<
      string | undefined
    >(undefined)
    const [fromViewOptions, setFromViewOptions] = useState<
      Array<Option> | undefined
    >(undefined)
    const [selectedFromViewId, setSelectedFromViewId] = useState<
      string | undefined
    >(undefined)
    const [addedViewName, setAddedViewName] = useState<string>('')
    const [actionMode, setActionMode] = useState<ActionMode>(ActionMode.Copy)
    const [toViewOptions, setToViewOptions] = useState<
      Array<Option> | undefined
    >(undefined)
    const [selectedToViewId, setSelectedToViewId] = useState<
      string | undefined
    >(undefined)
    const [isLoadingViews, setIsLoadingViews] = useState(false)

    const networkOptions = useMemo(
      () =>
        mergeDialogData?.networks.map((n) => ({
          label: n.name,
          value: n.id,
        })),
      [mergeDialogData?.networks],
    )

    const handleNetworkChange = useCallback(
      async (selectedOption: SingleValue<Option>) => {
        setSelectedNetworkId(selectedOption?.value)
        setSelectedFromViewId(undefined)
        setFromViewOptions(undefined)
        if (selectedOption) {
          setIsLoadingViews(true)
          const views = await getViewsByNetworkId(selectedOption.value)
          setIsLoadingViews(false)
          setFromViewOptions(
            views?.map((view) => ({
              value: view.id,
              label: view.name,
            })),
          )
        }
      },
      [getViewsByNetworkId],
    )

    const handleConfirm = useCallback(() => {
      if (!selectedFromViewId)
        throw new Error('In merge view dialog selectedViewId is not defined')
      if (actionMode === ActionMode.Copy) {
        void onConfirm(selectedFromViewId, {
          mode: { viewName: addedViewName ?? undefined, actionMode },
        })
      }
      if (actionMode === ActionMode.Merge) {
        if (!selectedToViewId)
          throw new Error(
            'In merge view dialog selectedToViewId is not defined',
          )
        void onConfirm(selectedFromViewId, {
          mode: { toViewId: selectedToViewId, actionMode },
        })
      }
      onClose()
    }, [
      actionMode,
      addedViewName,
      onClose,
      onConfirm,
      selectedFromViewId,
      selectedToViewId,
    ])

    const getValue = (
      options: Array<Option> | undefined,
      selectedValue: string | undefined,
    ) => options?.find((option) => option?.value === selectedValue)

    const getMergeValue = (
      options: Array<Option> | undefined,
      selectedValue: string | undefined,
      selectedFromOption: Option | undefined,
    ) => {
      if (!selectedValue) {
        return options?.find(
          (option) => option.label === selectedFromOption?.label,
        )
      }
      return getValue(options, selectedValue)
    }
    return (
      <DialogRoot
        open={!!mergeDialogData}
        onOpenChange={(e) => {
          if (!e.open) {
            onClose()
          }
        }}
      >
        <DialogContent ref={ref} aria-label="view-merge-dialog">
          <DialogHeader>
            <Flex alignItems="center">
              <DialogTitle>Merge view</DialogTitle>
              <InformationIcon info="For now, view merge can only drag the position of nodes from one view to another. Other modifications (e.g. probability, types...) will not be transferred." />
            </Flex>
          </DialogHeader>
          <DialogBody>
            <Flex direction="column" gap={4} minWidth={400} paddingX={6}>
              <Text fontWeight={600}>{`Choose "from network":`}</Text>
              <Select
                options={networkOptions}
                placeholder="Select network"
                value={getValue(networkOptions, selectedNetworkId)}
                onChange={handleNetworkChange}
              />

              <Text display="inline-block" fontWeight={600} paddingTop={4}>
                {`Choose "from view":`}
              </Text>

              <Select
                isDisabled={!fromViewOptions}
                isLoading={isLoadingViews}
                options={fromViewOptions}
                placeholder={
                  fromViewOptions?.length === 0
                    ? 'No view available'
                    : 'Select view'
                }
                value={getValue(fromViewOptions, selectedFromViewId)}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setSelectedFromViewId(selectedOption?.value)
                  }
                }}
              />
              <RadioGroup
                value={actionMode}
                onValueChange={async (details) => {
                  const value = ActionModeEnum.parse(details.value)
                  if (value === ActionMode.Merge && mergeDialogData) {
                    const views = await getViewsByNetworkId(
                      mergeDialogData.currentNetworkId,
                    )
                    setToViewOptions(
                      views.map((view) => ({
                        value: view.id,
                        label: view.name,
                      })),
                    )
                  }
                  setActionMode(value)
                }}
              >
                <Flex alignItems="center">
                  <HStack alignItems="flex-start" gap={4}>
                    <Radio value={ActionMode.Copy}>Copy</Radio>
                    <Radio value={ActionMode.Merge}>Merge</Radio>
                  </HStack>
                  <InformationIcon info="Copy create a brand new view in the network and add the nodes into it. The merge add the nodes to an already existing view. Both merge and copy only add nodes to the view that already exist in the network." />
                </Flex>
              </RadioGroup>
              {actionMode === ActionMode.Copy ? (
                <>
                  <Text fontWeight={600} marginTop={-2}>
                    {`Name "to view":`}
                  </Text>
                  <Input
                    placeholder="Leave it blank for auto naming"
                    type="text"
                    value={addedViewName}
                    onChange={(e) => setAddedViewName(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <Text fontWeight={600} marginTop={-2}>
                    {`Select "to view":`}
                  </Text>
                  <Select
                    isDisabled={!toViewOptions}
                    options={toViewOptions}
                    placeholder={
                      fromViewOptions?.length === 0
                        ? 'No view available'
                        : 'Select view'
                    }
                    value={getMergeValue(
                      toViewOptions,
                      selectedToViewId,
                      getValue(fromViewOptions, selectedFromViewId),
                    )}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedToViewId(selectedOption?.value)
                      }
                    }}
                  />
                </>
              )}
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button ref={cancelRef} variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!selectedFromViewId}
              ml={3}
              onClick={handleConfirm}
            >
              {actionMode === ActionMode.Copy ? 'Copy' : 'Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    )
  },
)

MergeViewDialog.displayName = 'MergeViewDialog'

export default MergeViewDialog
